/**
 * <TutorApplicationPage> — la candidature d'un tuteur, à la place de son accueil (page).
 *
 * Ce que voit un compte `tutor-candidate` sur l'onglet `dashboard` (l'ancien
 * `TutorApplicationScreen`) : tant que la candidature est en cours, le parcours en un seul chemin,
 * l'étape du moment ouverte en place (`<TutorApplicationJourney>`) ; une fois toutes les étapes
 * faites, où en est la candidature (`<TutorApplicationOutcome>`).
 *
 * Une étape vidéo ne s'enregistre pas au navigateur d'un téléphone ni sur Safari : l'ancien écran
 * ouvrait alors une boîte qui demande l'app. Elle est ici, contrôlée (`appRequired`), avec ses deux
 * réponses : « Télécharger » et « Retour ».
 *
 * Contrôlée de bout en bout : la candidature, le chargement, la boîte arrivent en props ; chaque
 * geste remonte.
 */
import React from "react"

import {
    type UITutorApplication,
    type UITutorApplicationAppRequired,
} from "../../../ports/primary/ui/types/tutorCandidature"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { TutorApplicationJourney, TutorApplicationJourneySkeleton } from "../../organisms/TutorApplicationJourney"
import { TutorApplicationOutcome } from "../../organisms/TutorApplicationOutcome"
import { TutorCandidateHeader } from "../../organisms/TutorCandidateHeader"
import { PageTemplate } from "../../templates/PageTemplate"

/** Une colonne : un chemin, pas un tableau de bord. */
const TUTOR_APPLICATION_WIDTH = 720

const TUTOR_APPLICATION_APP_TEXTS = {
    title: {
        "mobile-web": "Télécharge l'application mobile",
        safari: "Télécharge l'application mobile ou connecte-toi depuis le navigateur Google Chrome",
    } satisfies Record<UITutorApplicationAppRequired, string>,
    message: "Cela est nécessaire pour répondre à la question suivante en vidéo.",
    download: "Télécharger",
    back: "Retour",
}

export type TutorApplicationPageProps = {
    /** `null` tant que le compte n'est pas lu. */
    application: UITutorApplication | null
    /** Premier chargement : le squelette. */
    loading?: boolean
    /** Le compte se relit (au retour sur l'écran) : le bouton de l'étape tourne, rien d'autre. */
    refreshing?: boolean
    onStartStep: () => void
    onOpenAccount: () => void
    /** La boîte « Télécharge l'application mobile », ouverte. */
    appRequired?: UITutorApplicationAppRequired | null
    onDownloadApp: () => void
    onDismissAppRequired: () => void
    testID?: string
}

export function TutorApplicationPage(props: TutorApplicationPageProps) {
    const {
        application,
        loading = false,
        refreshing = false,
        onStartStep,
        onOpenAccount,
        appRequired = null,
        onDownloadApp,
        onDismissAppRequired,
        testID,
    } = props

    const current = application?.steps.find((step) => step.status === "current")
    const finished = application != null && (application.status !== "draft" || current == null)

    const header =
        application != null ? (
            <TutorCandidateHeader
                firstName={application.firstName}
                avatarUri={application.avatarUri}
                alert={application.emailUnverified}
                onOpenAccount={onOpenAccount}
            />
        ) : null

    return (
        <>
            <PageTemplate
                testID={testID}
                header={header}
                loading={loading}
                skeleton={<TutorApplicationJourneySkeleton />}
                padding="4"
                maxContentWidth={TUTOR_APPLICATION_WIDTH}
            >
                {application != null &&
                    (finished ? (
                        <TutorApplicationOutcome
                            status={application.status === "draft" ? "pending" : application.status}
                        />
                    ) : (
                        <TutorApplicationJourney steps={application.steps} loading={refreshing} onStart={onStartStep} />
                    ))}
            </PageTemplate>
            <ConfirmDialog
                visible={appRequired != null}
                title={appRequired != null ? TUTOR_APPLICATION_APP_TEXTS.title[appRequired] : ""}
                message={TUTOR_APPLICATION_APP_TEXTS.message}
                confirmLabel={TUTOR_APPLICATION_APP_TEXTS.download}
                cancelLabel={TUTOR_APPLICATION_APP_TEXTS.back}
                danger={false}
                onConfirm={onDownloadApp}
                onCancel={onDismissAppRequired}
            />
        </>
    )
}
